import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import { apiZodios } from '~api/apiZodios'

import { identifyAmplitudeUser, waitForAmplitude } from '../AmplitudeInit'
import { type RootState } from '../redux/rootReducer'

const useAmplitudeIdentification = (): void => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const user = useSelector((state: RootState) => state?.auth.user)
  const selectedOrganization = useSelector(
    (state: RootState) => state?.organization.selectedOrganization,
  )

  const lastSignatureRef = useRef<string | null>(null)
  const requestIdRef = useRef<number>(0)

  useEffect(() => {
    if (!userId) return

    const signature = JSON.stringify({
      userId,
      isSuper: !!user?.super,
      isInternal: !!user?.is_internal,
      orgId: selectedOrganization?.id ?? null,
      orgName: selectedOrganization?.name ?? null,
      orgIdentityId: selectedOrganization?.organization_id ?? null,
    })
    if (lastSignatureRef.current === signature) return
    lastSignatureRef.current = signature

    const currentRequestId = ++requestIdRef.current

    const identifyUser = async (): Promise<void> => {
      await waitForAmplitude()

      if (currentRequestId !== requestIdRef.current) return

      const userProperties: Record<string, string | boolean> = {
        user_type: user?.super ? 'superuser' : 'staff',
        is_internal: !!user?.is_internal,
      }

      if (selectedOrganization) {
        if (selectedOrganization.organization_id) {
          userProperties.organization_id = selectedOrganization.organization_id
        }
        if (selectedOrganization.name) {
          userProperties.organization_name = selectedOrganization.name
        }
      }

      try {
        const orgIds = selectedOrganization ? [selectedOrganization.id] : []
        if (orgIds.length === 0) throw new Error('No org selected')
        const { tiers } = await apiZodios.core_listOrgIdentitiesTier({
          user_id: userId,
          organization_identity_ids: orgIds,
        })
        if (currentRequestId !== requestIdRef.current) return
        const matchingTier = tiers[0]
        if (matchingTier) {
          userProperties.workspace_plan = matchingTier.tier
        }
      } catch {
        // Tier info unavailable; continue without it
      }

      if (currentRequestId !== requestIdRef.current) return
      identifyAmplitudeUser(userId, userProperties)
    }

    identifyUser()
  }, [
    userId,
    user?.super,
    user?.is_internal,
    selectedOrganization?.id,
    selectedOrganization?.name,
    selectedOrganization?.organization_id,
  ])
}

export default useAmplitudeIdentification
